// ---------------------------------------------------------------------------
// The local store. This is the app's source of truth while a canvasser is
// walking; Supabase is where it eventually gets to (PLAN 5).
//
// Everything the UI reads comes from here, so a visit is visible the instant it
// is recorded whether or not there is signal, and a force-quit loses nothing.
// ---------------------------------------------------------------------------

import Dexie, { type Table } from 'dexie';
import type {
  HouseNoteDraft,
  Household,
  Person,
  PersonContact,
  PersonContactDraft,
  Turf,
  VisitDraft,
} from '@yucanvas/shared';

/**
 * A visit on the device. `synced` is 0/1 rather than boolean because IndexedDB
 * cannot index booleans, and the pending count queries it on every write.
 */
export interface LocalVisit extends VisitDraft {
  /** Server clock, filled in by the pull. Null means the server has not seen it. */
  received_at: string | null;
  synced: 0 | 1;
  attempts: number;
  last_error: string | null;
  /**
   * Device clock (ms) before which a row the server rejected outright is not
   * sent again (offline/push-errors.ts retryDelay). Absent means send it.
   * Present on every queued row type, never pushed.
   */
  retry_at?: number;
}

/**
 * A household on the device.
 *
 * `synced` is absent on rows that came from the server -- they are, by
 * definition, already there. It is set to 0 only on a home a canvasser added
 * at a door, which has to be pushed like a visit. Dexie leaves records with an
 * undefined index value out of that index, so the pending query returns
 * exactly the hand-added ones.
 */
export interface LocalHousehold extends Household {
  synced?: 0 | 1;
  attempts?: number;
  last_error?: string | null;
  retry_at?: number;
  /**
   * 0 when this device has removed the home and the server has not been told
   * yet. Absent otherwise. Separate from `synced`, which tracks whether the
   * home was ever created -- a home can need creating, or removing, and they
   * are not the same queue.
   */
  pending_removal?: 0 | 1;
  /** 0 when this device has brought a removed home back and the server has not been told. */
  pending_revival?: 0 | 1;
}
export interface LocalTurf extends Turf {}

/**
 * A registered voter, cached on the device.
 *
 * This is the row that carries names, party, age and gender onto a canvasser's
 * personal phone, and it is what makes a lost handset reportable
 * (docs/campaign-requirements.md § 5.1). That question is still open; if it is
 * answered "online only", this table goes away and TurfDataProvider fetches
 * people over the network instead. Nothing else changes.
 */
export interface LocalPerson extends Person {
  /** Absent on voter-file rows; 0 on a resident recorded at the door. */
  synced?: 0 | 1;
  attempts?: number;
  last_error?: string | null;
  retry_at?: number;
}

/**
 * A house note on the device.
 *
 * `author_name` is not a server column -- it is resolved through an embed on
 * the pull and kept here so the sheet can say who wrote a note without the
 * device holding a copy of the roster.
 */
export interface LocalHouseNote extends HouseNoteDraft {
  received_at: string | null;
  author_name: string | null;
  synced: 0 | 1;
  attempts: number;
  last_error: string | null;
  retry_at?: number;
}

export interface LocalPersonContact extends PersonContactDraft {
  received_at: string | null;
  synced: 0 | 1;
  attempts: number;
  last_error: string | null;
  retry_at?: number;
}

export interface MetaRow {
  key: string;
  value: string;
}

class CanvassDb extends Dexie {
  visits!: Table<LocalVisit, string>;
  households!: Table<LocalHousehold, string>;
  turfs!: Table<LocalTurf, string>;
  meta!: Table<MetaRow, string>;
  people!: Table<LocalPerson, string>;
  personContacts!: Table<LocalPersonContact, string>;
  houseNotes!: Table<LocalHouseNote, string>;

  constructor() {
    super('yucanvas');
    this.version(1).stores({
      visits: 'id, household_id, canvasser_id, synced, visited_at',
      households: 'id, turf_id, updated_at',
      turfs: 'id',
      meta: 'key',
    });
    // v2 adds the voter file. Dexie carries the v1 stores forward untouched,
    // so a canvasser mid-shift keeps their queued visits across the upgrade.
    this.version(2).stores({
      people: 'id, household_id, updated_at',
      personContacts: 'id, person_id, visit_id, synced, contacted_at',
    });
    this.version(3).stores({
      houseNotes: 'id, household_id, synced, created_at',
    });
    // v4 indexes the pending flag on households and people, so a home added
    // at a door can be found and pushed.
    this.version(4).stores({
      households: 'id, turf_id, updated_at, synced',
      people: 'id, household_id, updated_at, synced',
    });
    this.version(5).stores({
      households: 'id, turf_id, updated_at, synced, pending_removal',
    });
    this.version(6).stores({
      households: 'id, turf_id, updated_at, synced, pending_removal, pending_revival',
    });
  }
}

export const db = new CanvassDb();

// --- cursors and bookkeeping ----------------------------------------------

/**
 * Delta-pull cursors. Server timestamps only: canvasser device clocks drift,
 * and a fast local clock would skip rows for good (PLAN 5.3).
 *
 * One per entity, not one shared. A single cursor took the maximum timestamp
 * across every table in the pull, so a visit received at 01:08:27 dragged the
 * cursor past a household flagged at 01:08:01 -- and that household, already
 * below the cursor, was never fetched again. The tables are written
 * independently and must be tracked independently.
 */
export const PULL_CURSORS = {
  households: 'pull_cursor_households',
  visits: 'pull_cursor_visits',
  people: 'pull_cursor_people',
  contacts: 'pull_cursor_contacts',
  notes: 'pull_cursor_notes',
} as const;

export type PullTable = keyof typeof PULL_CURSORS;
export const PULL_TABLES = Object.keys(PULL_CURSORS) as PullTable[];

/**
 * The rest of each table's position beside its cursor: the newest stamp seen
 * and when (offline/cursor.ts). Separate keys so a cursor written before these
 * existed is still read as-is.
 */
export const PULL_POSITIONS: Record<PullTable, string> = {
  households: 'pull_position_households',
  visits: 'pull_position_visits',
  people: 'pull_position_people',
  contacts: 'pull_position_contacts',
  notes: 'pull_position_notes',
};

/** The single cursor these replaced. Read once, to seed them on upgrade. */
export const PULL_CURSOR = 'pull_cursor';

/**
 * What the pull fetched, last time this device ran one.
 *
 * Bumped whenever the pull starts fetching something it did not fetch before,
 * or fetches it differently. A mismatch resets every cursor and re-pulls the
 * turf once, because a cursor is only a promise that everything before it was
 * already collected -- and after a change of shape, that promise is false.
 *
 * 2: people are scoped by turf. Before this they were fetched only for
 *    households that changed in the same pull, which in steady state is none,
 *    so devices carrying a recent cursor had never stored a single voter.
 * 3: house notes became rows and are pulled.
 * 4: evictDeparted() asked about every cached home in one call, and the
 *    server's 1,000-row cap cut the answer short, so a device holding more
 *    lost the rest -- visits too -- and no later delta pull brought them back.
 */
/**
 * The assigned turf ids the last successful pull covered, sorted and joined.
 *
 * The delta pull filters households on `updated_at > cursor`, but turf is
 * delivered unfiltered -- so a canvasser assigned to a turf that was populated
 * days ago got the turf row and none of its households, and the app showed
 * "0 of 0 homes" with nothing to download. Gaining a turf has to reset the
 * cursors the same way a shape change does.
 */
export const PULL_TURFS = 'pull_turfs';

export const PULL_SHAPE = 'pull_shape';
export const PULL_SHAPE_VERSION = '4';
export const LAST_SYNC = 'last_sync_at';
export const EPOCH = '1970-01-01T00:00:00.000Z';

export async function getMeta(key: string): Promise<string | null> {
  const row = await db.meta.get(key);
  return row?.value ?? null;
}

export async function setMeta(key: string, value: string): Promise<void> {
  await db.meta.put({ key, value });
}

/** Visits accepted on this device that the server has not acknowledged. */
export function pendingVisits(): Promise<LocalVisit[]> {
  return db.visits.where('synced').equals(0).toArray();
}

export function pendingContacts(): Promise<LocalPersonContact[]> {
  return db.personContacts.where('synced').equals(0).toArray();
}

export function pendingNotes(): Promise<LocalHouseNote[]> {
  return db.houseNotes.where('synced').equals(0).toArray();
}

export function pendingHouseholds(): Promise<LocalHousehold[]> {
  return db.households.where('synced').equals(0).toArray();
}

export function pendingPeople(): Promise<LocalPerson[]> {
  return db.people.where('synced').equals(0).toArray();
}

export function pendingRemovals(): Promise<LocalHousehold[]> {
  return db.households.where('pending_removal').equals(0).toArray();
}

export function pendingRevivals(): Promise<LocalHousehold[]> {
  return db.households.where('pending_revival').equals(0).toArray();
}

/**
 * Everything this device is holding that the server has not acknowledged.
 * Visits and contacts are counted together: a canvasser does not care which
 * table a knock landed in, only whether their shift has uploaded.
 */
export async function pendingCount(): Promise<number> {
  const [visits, contacts, notes, households, people, removals, revivals] = await Promise.all([
    db.visits.where('synced').equals(0).count(),
    db.personContacts.where('synced').equals(0).count(),
    db.houseNotes.where('synced').equals(0).count(),
    db.households.where('synced').equals(0).count(),
    db.people.where('synced').equals(0).count(),
    db.households.where('pending_removal').equals(0).count(),
    db.households.where('pending_revival').equals(0).count(),
  ]);
  return visits + contacts + notes + households + people + removals + revivals;
}

/**
 * Wipes the device store.
 *
 * This began as courtesy on a shared phone. Since the campaign adopted the
 * voter file it is a control: the store now holds names, party, age and gender
 * for every voter in the canvasser's turf, on a contractor's personal device
 * (docs/campaign-requirements.md § 5.1). Anything added to the schema must be
 * cleared here too.
 */
export async function clearLocalData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.visits,
      db.households,
      db.turfs,
      db.meta,
      db.people,
      db.personContacts,
      db.houseNotes,
    ],
    async () => {
      await Promise.all([
        db.visits.clear(),
        db.households.clear(),
        db.turfs.clear(),
        db.meta.clear(),
        db.people.clear(),
        db.personContacts.clear(),
        db.houseNotes.clear(),
      ]);
    },
  );
}
